/**
 * Cost calculator — plan limits, add-ons, and recommendation logic.
 * Static catalog matches the current /plan_limit payload. A successful API response
 * replaces plan prices, included limits, and pay-as-you-go rates.
 */

export const NEED_TYPES = {
  CHAT: 'chat',
  INBOUND: 'inbound_voice',
  OUTBOUND: 'outbound_voice',
  VOICE_CHAT: 'voice_chat',
};

export const PLAN_API_KEYS = {
  free: 'Free',
  payg: 'Pay_As_You_Go',
  basic: 'Starter_Chat',
  standard: 'Starter_Voice',
  advance: 'Professional',
  pro: 'Enterprise',
};

/** Channel keys used in the calculator UI */
export const CHANNEL_ADDON_CATALOG = {
  whatsapp: {
    addonKey: 'WhatsAppAiAgent',
    label: 'WhatsApp AI Chat Agent',
    price: 199,
    messageLimit: 11000,
  },
  instagram: {
    addonKey: 'InstagramAiAgent',
    label: 'Instagram AI Agent',
    price: 49,
    messageLimit: 11000,
  },
  facebook: {
    addonKey: 'FacebookAiAgent',
    label: 'Facebook AI Agent',
    price: 49,
    messageLimit: 11000,
  },
  email: {
    addonKey: 'EmailAiAgent',
    label: 'Email AI Agent',
    price: 99,
    messageLimit: 50000,
  },
  line: {
    addonKey: 'LineAiAgent',
    label: 'Line AI Agent',
    price: 99,
    messageLimit: 11000,
  },
  sms: {
    addonKey: 'SmsAiAgent',
    label: 'SMS AI Agent',
    price: 99,
    messageLimit: 11000,
  },
  wechat: {
    addonKey: 'WeChatAiAgent',
    label: 'WeChat AI Agent',
    price: 99,
    messageLimit: 11000,
  },
  web: {
    addonKey: null,
    label: 'Website / Web Chat',
    price: 0,
    messageLimit: Infinity,
  },
};

const PLANS_WITH_WEB_CHAT = new Set([
  'Free',
  'Starter_Chat',
  'Professional',
  'Enterprise',
  'Pay_As_You_Go',
]);

/** Static limits shaped like /plan_limit API — used when API is unavailable */
export const CALCULATOR_STATIC_PLAN_LIMITS = {
  Free: {
    PRICE: 0,
    MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 22,
    MAX_INBOUND_CALL_DURATION_MINUTES: 30,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AGENT_CREATION_LIMIT: 2,
    MAX_CHAT_AGENT_CREATION_LIMIT: 2,
    MAX_CONCURRENT_CALLS: 3,
  },
  Pay_As_You_Go: {
    PRICE: 20,
    MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 22000,
    MAX_INBOUND_CALL_DURATION_MINUTES: 500,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AGENT_CREATION_LIMIT: 2,
    MAX_CHAT_AGENT_CREATION_LIMIT: 2,
    MAX_CONCURRENT_CALLS: 3,
    CHARGE_PER_MESSAGE: 0.01,
    CHARGE_PER_MINUTE: 0.2,
  },
  Starter_Chat: {
    PRICE: 99,
    MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 8250,
    MAX_INBOUND_CALL_DURATION_MINUTES: 350,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AGENT_CREATION_LIMIT: 5,
    MAX_CHAT_AGENT_CREATION_LIMIT: 5,
    MAX_CONCURRENT_CALLS: 3,
    CHARGE_PER_MESSAGE: 0.01,
    MAX_CUSTOM_MESSAGES: 660000,
  },
  Starter_Voice: {
    PRICE: 129,
    MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 11000,
    MAX_INBOUND_CALL_DURATION_MINUTES: 500,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AGENT_CREATION_LIMIT: 5,
    MAX_CHAT_AGENT_CREATION_LIMIT: 20,
    MAX_CONCURRENT_CALLS: 3,
    CHARGE_PER_MINUTE: 0.14,
    MAX_CUSTOM_MINUTES: 20000,
  },
  Professional: {
    PRICE: 169,
    MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 11000,
    MAX_INBOUND_CALL_DURATION_MINUTES: 500,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AGENT_CREATION_LIMIT: 10,
    MAX_CHAT_AGENT_CREATION_LIMIT: 10,
    MAX_CONCURRENT_CALLS: 3,
    CHARGE_PER_MESSAGE: 0.01,
    CHARGE_PER_MINUTE: 0.14,
    MAX_CUSTOM_MESSAGES: 44000,
    MAX_CUSTOM_MINUTES: 2000,
  },
  Enterprise: {
    PRICE: 749,
    MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 27500,
    MAX_INBOUND_CALL_DURATION_MINUTES: 1000,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 1000,
    MAX_VOICE_AGENT_CREATION_LIMIT: 20,
    MAX_CHAT_AGENT_CREATION_LIMIT: 20,
    MAX_CONCURRENT_CALLS: 3,
    CHARGE_PER_MESSAGE: 0.01,
    CHARGE_PER_MINUTE: 0.14,
    MAX_CUSTOM_MESSAGES: 600000,
    MAX_CUSTOM_MINUTES: 40000,
  },
  Addons: {
    WhatsAppAiAgent: { PRICE: 199, MAX_CHAT_MESSAGES_FOR_WHATSAPP_CHAT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.018, MAX_CUSTOM_MESSAGES: 22000 },
    InstagramAiAgent: { PRICE: 49, MAX_CHAT_MESSAGES_FOR_INSTAGRAM_CHAT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.0045, MAX_CUSTOM_MESSAGES: 220000 },
    FacebookAiAgent: { PRICE: 49, MAX_CHAT_MESSAGES_FOR_FACEBOOK_CHAT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.0045, MAX_CUSTOM_MESSAGES: 220000 },
    EmailAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_EMAIL_CHAT_LIMIT: 50000, CHARGE_PER_MESSAGE: 0.002, MAX_CUSTOM_MESSAGES: 550000 },
    LineAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_LINE_CHAT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.009, MAX_CUSTOM_MESSAGES: 220000 },
    SmsAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_SMS_CHAT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.009, MAX_CUSTOM_MESSAGES: 220000 },
    WeChatAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_WECHAT_CHAT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.009, MAX_CUSTOM_MESSAGES: 220000 },
    WhatsAppVoiceAgent: { PRICE: 99, MAX_CALL_DURATION_MINUTES_PER_TENANT: 350, CHARGE_PER_MINUTE: 0.28, MAX_CUSTOM_MINUTES: 50000 },
    VoiceCallRecording: { PRICE: 49, MAX_CALL_RECORDING_MINUTES: 1000, CHARGE_PER_MINUTE: 0.049, MAX_CUSTOM_MINUTES: 100000 },
    CsmConversations: { PRICE: 25, MAX_CSM_CONVERSATIONS_LIMIT: 1000 },
    WebAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT: 11000, CHARGE_PER_MESSAGE: 0.006, MAX_CUSTOM_MESSAGES: 220000 },
  },
};

export const STATIC_PLAN_METRICS = {
  Free: {
    price: 0,
    chatSessions: 22,
    inboundMinutes: 30,
    outboundMinutes: 0,
    voiceAgents: 2,
    chatAgents: 2,
    chargePerChat: 0,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'FREE',
    subtitle: 'Basic access for individuals and testing',
  },
  Pay_As_You_Go: {
    price: 20,
    chatSessions: Infinity,
    inboundMinutes: Infinity,
    outboundMinutes: Infinity,
    voiceAgents: 2,
    chatAgents: 2,
    chargePerChat: 0.01,
    chargePerMinute: 0.2,
    minDeposit: 20,
    displayName: 'PAY AS YOU GO',
    subtitle: 'Flexible pricing based on usage',
  },
  Starter_Chat: {
    price: 99,
    chatSessions: 8250,
    inboundMinutes: 350,
    outboundMinutes: 0,
    voiceAgents: 5,
    chatAgents: 5,
    chargePerChat: 0.01,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'BASIC',
    subtitle: 'Perfect for small teams getting started with AI chat',
  },
  Starter_Voice: {
    price: 129,
    chatSessions: 11000,
    inboundMinutes: 500,
    outboundMinutes: 0,
    voiceAgents: 5,
    chatAgents: 20,
    chargePerChat: 0,
    chargePerMinute: 0.14,
    minDeposit: 0,
    displayName: 'STANDARD',
    subtitle: 'Voice AI capabilities for customer service',
  },
  Professional: {
    price: 169,
    chatSessions: 11000,
    inboundMinutes: 500,
    outboundMinutes: 0,
    voiceAgents: 10,
    chatAgents: 10,
    chargePerChat: 0.01,
    chargePerMinute: 0.14,
    minDeposit: 0,
    displayName: 'ADVANCE',
    subtitle: 'Advanced features for growing businesses',
  },
  Enterprise: {
    price: 749,
    chatSessions: 27500,
    inboundMinutes: 1000,
    outboundMinutes: 1000,
    voiceAgents: 20,
    chatAgents: 20,
    chargePerChat: 0.01,
    chargePerMinute: 0.14,
    minDeposit: 0,
    displayName: 'PRO',
    subtitle: 'Complete solution for large organizations',
  },
};

const CHAT_LIMIT_KEYS = [
  'MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT',
  'MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT',
  'MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT',
];

const INBOUND_MINUTE_KEYS = [
  'MAX_INBOUND_CALL_DURATION_MINUTES',
  'MAX_INBOUND_CALL_DURATION_MINUTES_PER_TENANT',
];

const OUTBOUND_MINUTE_KEYS = [
  'MAX_OUTBOUND_CALL_DURATION_MINUTES',
  'MAX_OUTBOUND_CALL_DURATION_MINUTES_PER_TENANT',
];

const WEB_CHAT_ADDON_KEYS = ['WebAiAgent', 'Starter_Voice_WebChat_Addon'];

const FIXED_PLANS = [
  'Starter_Chat',
  'Starter_Voice',
  'Professional',
  'Enterprise',
];

const PLAN_TIER = {
  Free: 0,
  Starter_Chat: 1,
  Starter_Voice: 2,
  Professional: 3,
  Enterprise: 4,
};

export const INCLUDED_CONCURRENT_CALLS = 3;

const hasPaidChatChannels = (channels = []) =>
  (Array.isArray(channels) ? channels : []).some((channel) => channel !== 'web');

export const anyFixedPlanFitsUsage = (usage, planLimits) => {
  const limits = mergePlanLimits(planLimits);
  const channels = usage.channels || [];
  return ['Free', ...FIXED_PLANS].some(
    (planKey) => quoteFixedPlan(planKey, { ...usage, channels }, limits) != null
  );
};

export const evaluateSalesContact = (usage, planLimits, concurrentCalls = INCLUDED_CONCURRENT_CALLS) => {
  const reasons = [];

  if (concurrentCalls > INCLUDED_CONCURRENT_CALLS) {
    reasons.push(
      `You need ${formatCount(concurrentCalls)} concurrent calls. Monthly plans include up to ${INCLUDED_CONCURRENT_CALLS}.`
    );
  }

  const fixedFits = anyFixedPlanFitsUsage(usage, planLimits);
  const paidChannels = hasPaidChatChannels(usage.channels);

  if (!fixedFits && paidChannels) {
    reasons.push(
      'Your chat volume exceeds monthly plan limits, and Pay As You Go does not support channel add-ons. Our team can build a custom bundle for you.'
    );
  }

  return {
    needsSales: reasons.length > 0,
    reasons,
  };
};

const parseMoney = (value) => {
  if (value == null || value === '-' || value === '') return null;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const firstFinite = (values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const resolveCount = (candidates, fallback) => {
  const fromApi = firstFinite(candidates);
  if (fromApi != null && fromApi >= 0) return fromApi;
  if (fallback === Infinity) return Infinity;
  return Number(fallback) || 0;
};

const isPricingPlanRecord = (value) =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (Object.prototype.hasOwnProperty.call(value, 'PRICE') ||
    Object.prototype.hasOwnProperty.call(value, 'price'));

export const mergePlanLimits = (apiLimits) => {
  const merged = {
    ...CALCULATOR_STATIC_PLAN_LIMITS,
    Addons: { ...CALCULATOR_STATIC_PLAN_LIMITS.Addons },
  };

  if (!apiLimits || typeof apiLimits !== 'object') return merged;

  Object.keys(CALCULATOR_STATIC_PLAN_LIMITS).forEach((planKey) => {
    if (planKey === 'Addons') return;
    const incoming = apiLimits[planKey];
    if (!isPricingPlanRecord(incoming)) return;
    merged[planKey] = { ...incoming };
  });

  if (apiLimits.Addons && typeof apiLimits.Addons === 'object') {
    merged.Addons = { ...merged.Addons, ...apiLimits.Addons };
  }

  return merged;
};

export const extractPlanMetrics = (planKey, planLimits) => {
  const limits = mergePlanLimits(planLimits);
  const fallback = STATIC_PLAN_METRICS[planKey] || STATIC_PLAN_METRICS.Free;
  const data = limits[planKey];
  if (!data || typeof data !== 'object') return { ...fallback, planKey };

  const chatSessions = resolveCount(
    CHAT_LIMIT_KEYS.map((key) => data[key]),
    fallback.chatSessions
  );

  const inboundMinutes = resolveCount(
    [
      ...INBOUND_MINUTE_KEYS.map((key) => data[key]),
      data.MAX_INBOUND_CALL_DURATION_SECONDS != null && data.MAX_INBOUND_CALL_DURATION_SECONDS !== ''
        ? Number(data.MAX_INBOUND_CALL_DURATION_SECONDS) / 60
        : undefined,
    ],
    fallback.inboundMinutes
  );

  const outboundMinutes = resolveCount(
    [
      ...OUTBOUND_MINUTE_KEYS.map((key) => data[key]),
      data.MAX_OUTBOUND_CALL_DURATION_SECONDS != null && data.MAX_OUTBOUND_CALL_DURATION_SECONDS !== ''
        ? Number(data.MAX_OUTBOUND_CALL_DURATION_SECONDS) / 60
        : undefined,
    ],
    fallback.outboundMinutes
  );

  const chargePerChat =
    parseMoney(data.CHARGE_PER_CHAT_SESSION) ??
    parseMoney(data.CHARGE_PER_SESSION) ??
    parseMoney(data.CHARGE_PER_MESSAGE) ??
    fallback.chargePerChat;

  const chargePerMinute =
    parseMoney(data.CHARGE_PER_MINUTE) ??
    parseMoney(data.CHARGE_PER_CALL_MINUTE) ??
    fallback.chargePerMinute;

  const voiceFromApi = firstFinite([
    data.MAX_VOICE_AGENT_CREATION_LIMIT,
    data.MAX_VOICE_AI_AGENT_CREATION_LIMIT,
    data.MAX_AGENT_CREATION_LIMIT,
  ]);
  const chatAgentsFromApi = firstFinite([
    data.MAX_CHAT_AGENT_CREATION_LIMIT,
    data.MAX_CHAT_AI_AGENT_CREATION_LIMIT,
  ]);

  const isPayg = planKey === 'Pay_As_You_Go';
  const price = parseMoney(data.PRICE) ?? fallback.price;

  return {
    planKey,
    price,
    chatSessions: isPayg ? Infinity : chatSessions,
    inboundMinutes: isPayg ? Infinity : inboundMinutes,
    outboundMinutes: isPayg ? Infinity : outboundMinutes,
    voiceAgents: voiceFromApi != null && voiceFromApi >= 0 ? voiceFromApi : fallback.voiceAgents,
    chatAgents:
      chatAgentsFromApi != null && chatAgentsFromApi >= 0 ? chatAgentsFromApi : fallback.chatAgents,
    chargePerChat,
    chargePerMinute,
    minDeposit: price > 0 ? price : fallback.minDeposit,
    maxCustomMessages: firstFinite([data.MAX_CUSTOM_MESSAGES]),
    maxCustomMinutes: firstFinite([data.MAX_CUSTOM_MINUTES]),
    displayName: fallback.displayName,
    subtitle: fallback.subtitle,
  };
};

export const getUsageFlags = (needType) => ({
  usesChat: needType === NEED_TYPES.CHAT || needType === NEED_TYPES.VOICE_CHAT,
  usesInbound: needType === NEED_TYPES.INBOUND || needType === NEED_TYPES.VOICE_CHAT,
  usesOutbound: needType === NEED_TYPES.OUTBOUND || needType === NEED_TYPES.VOICE_CHAT,
});

export const planSupportsNeedType = (metrics, needType, usage = {}) => {
  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(needType);
  const chatNeeded = usesChat && (usage.monthlyChats || 0) > 0;
  const inboundNeeded = usesInbound && (usage.inboundMinutes || 0) > 0;
  const outboundNeeded = usesOutbound && (usage.outboundMinutes || 0) > 0;

  if (chatNeeded && metrics.chatSessions <= 0 && metrics.planKey !== 'Pay_As_You_Go') return false;
  if (inboundNeeded && metrics.inboundMinutes <= 0 && metrics.planKey !== 'Pay_As_You_Go') {
    return false;
  }
  if (outboundNeeded && metrics.outboundMinutes <= 0 && metrics.planKey !== 'Pay_As_You_Go') {
    return false;
  }
  return true;
};

export const usageFitsPlan = (usage, metrics) => {
  if (metrics.planKey === 'Pay_As_You_Go') return true;

  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(usage.needType);

  if (usesChat && usage.monthlyChats > 0 && usage.monthlyChats > metrics.chatSessions) {
    return false;
  }
  if (usesInbound && usage.inboundMinutes > 0 && usage.inboundMinutes > metrics.inboundMinutes) {
    return false;
  }
  if (usesOutbound && usage.outboundMinutes > 0 && usage.outboundMinutes > metrics.outboundMinutes) {
    return false;
  }
  return true;
};

const planSupportsWebChat = (planKey) => PLANS_WITH_WEB_CHAT.has(planKey);

const getAddonPrice = (addonKey, planLimits) => {
  const limits = mergePlanLimits(planLimits);
  const fromApi = limits.Addons?.[addonKey];
  const staticFallback = CALCULATOR_STATIC_PLAN_LIMITS.Addons?.[addonKey];
  return (
    parseMoney(fromApi?.PRICE ?? fromApi?.price) ??
    parseMoney(staticFallback?.PRICE ?? staticFallback?.price) ??
    0
  );
};

/** Monthly add-on cost for selected chat channels on a given base plan */
export const calculateChannelAddonCost = (planKey, channels = [], planLimits) => {
  const selected = Array.isArray(channels) ? channels : [];
  const items = [];
  let cost = 0;

  const needsWeb =
    selected.includes('web') &&
    !planSupportsWebChat(planKey);

  if (needsWeb) {
    const webAddonKey = WEB_CHAT_ADDON_KEYS.find((key) => getAddonPrice(key, planLimits) > 0);
    const webAddonPrice = webAddonKey ? getAddonPrice(webAddonKey, planLimits) : 0;
    if (webAddonKey && webAddonPrice > 0) {
      cost += webAddonPrice;
      items.push({
        key: webAddonKey,
        label: 'Web Chat Add-on',
        price: webAddonPrice,
      });
    }
  }

  selected.forEach((channelKey) => {
    if (channelKey === 'web') return;
    const catalog = CHANNEL_ADDON_CATALOG[channelKey];
    if (!catalog?.addonKey) return;

    const price = getAddonPrice(catalog.addonKey, planLimits) || catalog.price;
    cost += price;
    items.push({
      key: catalog.addonKey,
      label: catalog.label,
      price,
    });
  });

  return { cost, items };
};

const roundMoney = (amount) => Math.round((Number(amount) + Number.EPSILON) * 100) / 100;

const formatRate = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '$0';
  const text = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  const [whole, frac = ''] = text.split('.');
  const digits = (frac + '00').slice(0, Math.max(2, frac.length));
  return `$${whole}.${digits}`;
};

const usageOverage = (used, included, rate, maxCustom) => {
  const cap = Number(included);
  if (!Number.isFinite(cap)) return { ok: false, extra: 0, units: 0 };
  if (used <= cap) return { ok: true, extra: 0, units: 0 };
  const charge = Number(rate) || 0;
  if (charge <= 0) return { ok: false, extra: 0, units: 0 };
  if (maxCustom != null && Number.isFinite(Number(maxCustom)) && used > Number(maxCustom)) {
    return { ok: false, extra: 0, units: 0 };
  }
  const units = used - cap;
  return { ok: true, extra: roundMoney(units * charge), units };
};

const readAddonIncluded = (addonData, fallback = 0) => {
  if (!addonData || typeof addonData !== 'object') return fallback;
  const match = Object.entries(addonData).find(([key, value]) => {
    if (!/^MAX_/i.test(key) || /CUSTOM|CHARGE|PRICE/i.test(key)) return false;
    const n = Number(value);
    return Number.isFinite(n) && n > 0;
  });
  return match ? Number(match[1]) : fallback;
};

const quoteAddon = (addonKey, label, used, planLimits, catalog) => {
  const data = mergePlanLimits(planLimits).Addons?.[addonKey];
  const price = getAddonPrice(addonKey, planLimits) || catalog?.price || 0;
  if (!(price > 0)) return null;
  const included = readAddonIncluded(data, catalog?.messageLimit || 0);
  const rate =
    parseMoney(data?.CHARGE_PER_MESSAGE ?? data?.charge_per_message) ??
    parseMoney(data?.CHARGE_PER_MINUTE ?? data?.charge_per_minute) ??
    0;
  const maxCustom = firstFinite([data?.MAX_CUSTOM_MESSAGES, data?.MAX_CUSTOM_MINUTES]);
  const over = used > 0 ? usageOverage(used, included, rate, maxCustom) : { ok: true, extra: 0, units: 0 };
  if (!over.ok) return null;
  const lines = [`${label} ${formatUsd(price)}`];
  if (over.extra > 0) {
    lines.push(`${formatCount(over.units)} extra chats × ${formatRate(rate)}`);
  }
  return {
    cost: roundMoney(price + over.extra),
    lines,
    item: { key: addonKey, label, price: roundMoney(price + over.extra) },
  };
};

const quoteFixedPlan = (planKey, usage, planLimits) => {
  const metrics = extractPlanMetrics(planKey, planLimits);
  const flags = getUsageFlags(usage.needType);
  const channels = Array.isArray(usage.channels) ? usage.channels : [];
  const chats = flags.usesChat ? Number(usage.monthlyChats) || 0 : 0;
  const inbound = flags.usesInbound ? Number(usage.inboundMinutes) || 0 : 0;
  const outbound = flags.usesOutbound ? Number(usage.outboundMinutes) || 0 : 0;
  const wantsWeb = chats > 0 && channels.includes('web');
  const paidChannels = channels.filter((channel) => channel !== 'web');

  if (chats > 0 && !wantsWeb && paidChannels.length === 0) return null;
  if (inbound > 0 && !(metrics.inboundMinutes > 0)) return null;
  if (outbound > 0 && !(metrics.outboundMinutes > 0)) return null;
  if (planKey === 'Free' && paidChannels.length > 0) return null;

  let cost = metrics.price || 0;
  let addonCost = 0;
  const addonItems = [];
  const breakdown = [];
  if (cost > 0) breakdown.push(`Plan ${formatUsd(cost)}`);

  if (wantsWeb && planSupportsWebChat(planKey)) {
    const over = usageOverage(
      chats,
      metrics.chatSessions,
      metrics.chargePerChat,
      metrics.maxCustomMessages
    );
    if (!over.ok) return null;
    if (over.extra > 0) {
      cost += over.extra;
      breakdown.push(`${formatCount(over.units)} extra chats × ${formatRate(metrics.chargePerChat)}`);
    }
  } else if (wantsWeb) {
    const webAddon = quoteAddon('WebAiAgent', 'Web Chat Add-on', chats, planLimits, {
      price: 99,
      messageLimit: 11000,
    });
    if (!webAddon) return null;
    cost += webAddon.cost;
    addonCost += webAddon.cost;
    addonItems.push(webAddon.item);
    breakdown.push(...webAddon.lines);
  }

  for (const channelKey of paidChannels) {
    const catalog = CHANNEL_ADDON_CATALOG[channelKey];
    if (!catalog?.addonKey) return null;
    const addon = quoteAddon(catalog.addonKey, catalog.label, chats, planLimits, catalog);
    if (!addon) return null;
    cost += addon.cost;
    addonCost += addon.cost;
    addonItems.push(addon.item);
    breakdown.push(...addon.lines);
  }

  if (inbound > 0) {
    const over = usageOverage(
      inbound,
      metrics.inboundMinutes,
      metrics.chargePerMinute,
      metrics.maxCustomMinutes
    );
    if (!over.ok) return null;
    if (over.extra > 0) {
      cost += over.extra;
      breakdown.push(`${formatCount(over.units)} extra inbound min × ${formatRate(metrics.chargePerMinute)}`);
    }
  }

  if (outbound > 0) {
    const over = usageOverage(
      outbound,
      metrics.outboundMinutes,
      metrics.chargePerMinute,
      metrics.maxCustomMinutes
    );
    if (!over.ok) return null;
    if (over.extra > 0) {
      cost += over.extra;
      breakdown.push(`${formatCount(over.units)} extra outbound min × ${formatRate(metrics.chargePerMinute)}`);
    }
  }

  return {
    planKey,
    metrics,
    basePlanCost: metrics.price || 0,
    addonCost,
    addonItems,
    monthlyCost: roundMoney(cost),
    isUsageBased: false,
    breakdown,
    tier: PLAN_TIER[planKey] ?? 0,
  };
};

const quotePaygPlan = (usage, planLimits) => {
  const flags = getUsageFlags(usage.needType);
  const channels = Array.isArray(usage.channels) ? usage.channels : [];
  if (channels.some((channel) => channel !== 'web')) return null;

  const chats = flags.usesChat ? Number(usage.monthlyChats) || 0 : 0;
  const inbound = flags.usesInbound ? Number(usage.inboundMinutes) || 0 : 0;
  const outbound = flags.usesOutbound ? Number(usage.outboundMinutes) || 0 : 0;
  if (outbound > 0) return null;
  if (chats <= 0 && inbound <= 0) return null;

  const metrics = extractPlanMetrics('Pay_As_You_Go', planLimits);
  const chatRate = metrics.chargePerChat ?? 0.01;
  const minuteRate = metrics.chargePerMinute ?? 0.2;
  let usageCost = 0;
  const breakdown = [];
  if (chats > 0) {
    usageCost += chats * chatRate;
    breakdown.push(`${formatCount(chats)} chats × ${formatRate(chatRate)}`);
  }
  if (inbound > 0) {
    usageCost += inbound * minuteRate;
    breakdown.push(`${formatCount(inbound)} inbound min × ${formatRate(minuteRate)}`);
  }
  const minimum = metrics.minDeposit || metrics.price || 20;
  if (usageCost < minimum) breakdown.push(`Minimum deposit ${formatUsd(minimum)}`);

  return {
    planKey: 'Pay_As_You_Go',
    metrics,
    basePlanCost: minimum,
    addonCost: 0,
    addonItems: [],
    monthlyCost: roundMoney(Math.max(usageCost, minimum)),
    isUsageBased: true,
    breakdown,
    tier: 2.5,
  };
};

export const calculatePaygMonthlyCost = (usage, metrics, channelAddonCost = 0) => {
  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(usage.needType);
  let cost = 0;
  const chatRate = metrics.chargePerChat ?? 0.01;
  const minuteRate = metrics.chargePerMinute ?? 0.2;
  if (usesChat) cost += (usage.monthlyChats || 0) * chatRate;
  if (usesInbound) cost += (usage.inboundMinutes || 0) * minuteRate;
  if (usesOutbound) cost += (usage.outboundMinutes || 0) * minuteRate;
  return Math.max(cost, metrics.minDeposit || metrics.price || 20) + channelAddonCost;
};

export const calculateFixedPlanCost = (usage, metrics, channelAddonCost = 0) => {
  if (metrics.planKey === 'Free' && metrics.price === 0) return channelAddonCost;
  return (metrics.price || 0) + channelAddonCost;
};

export const getPlanLimitSummary = (metrics, usage) => {
  const parts = [];
  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(usage?.needType || '');
  const showFullProLimits = metrics.planKey === 'Enterprise';

  if (
    (usesChat || showFullProLimits) &&
    metrics.chatSessions > 0 &&
    metrics.chatSessions !== Infinity
  ) {
    parts.push(`${formatCount(metrics.chatSessions)} chats`);
  }
  if (
    (usesInbound || showFullProLimits) &&
    metrics.inboundMinutes > 0 &&
    metrics.inboundMinutes !== Infinity
  ) {
    parts.push(`${formatCount(metrics.inboundMinutes)} inbound min`);
  }
  if (
    (usesOutbound || showFullProLimits) &&
    metrics.outboundMinutes > 0 &&
    metrics.outboundMinutes !== Infinity
  ) {
    parts.push(`${formatCount(metrics.outboundMinutes)} outbound min`);
  }

  return parts.length ? `Includes ${parts.join(' · ')}` : null;
};

export const recommendPricingPlan = (usage, planLimits, options = {}) => {
  const limits = mergePlanLimits(planLimits);
  const channels = usage.channels || (getUsageFlags(usage.needType).usesChat ? ['web'] : []);
  const normalizedUsage = { ...usage, channels };
  const concurrentCalls = options.concurrentCalls ?? INCLUDED_CONCURRENT_CALLS;
  const salesContact = evaluateSalesContact(normalizedUsage, limits, concurrentCalls);

  const quotes = ['Free', ...FIXED_PLANS]
    .map((planKey) => quoteFixedPlan(planKey, normalizedUsage, limits))
    .filter(Boolean);
  const paygQuote = quotePaygPlan(normalizedUsage, limits);
  if (paygQuote) quotes.push(paygQuote);

  quotes.sort((a, b) => a.monthlyCost - b.monthlyCost || Number(a.isUsageBased) - Number(b.isUsageBased) || a.tier - b.tier);

  const chosen = quotes[0];
  const attachSales = (result) => ({
    ...result,
    salesContact,
    paygAlternativeCost: null,
    fixedAlternative: null,
    limitSummary: result.isUsageBased ? null : getPlanLimitSummary(result.metrics, normalizedUsage),
    note: null,
  });

  if (chosen) return attachSales({ ...chosen, hideEstimate: false });

  const enterpriseMetrics = extractPlanMetrics('Enterprise', limits);
  return {
    planKey: 'Enterprise',
    metrics: enterpriseMetrics,
    basePlanCost: 0,
    addonCost: 0,
    addonItems: [],
    monthlyCost: 0,
    isUsageBased: false,
    breakdown: [],
    hideEstimate: true,
    tier: PLAN_TIER.Enterprise,
    paygAlternativeCost: null,
    fixedAlternative: null,
    limitSummary: null,
    note: null,
    salesContact: {
      needsSales: true,
      reasons: [
        ...salesContact.reasons,
        'This volume is above the published plan limits. Contact sales for a custom quote.',
      ],
    },
  };
};

export const formatUsd = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '$0';
  const rounded = Math.round(n * 100) / 100;
  const hasCents = Math.abs(rounded - Math.trunc(rounded)) > 0.001;
  return hasCents
    ? `$${rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${rounded.toLocaleString()}`;
};

export const formatCount = (num) => Number(num || 0).toLocaleString();
